import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { CalendarIcon, EyeIcon, XIcon, AlertCircleIcon, ChevronDownIcon, CheckCircleIcon, AlertTriangleIcon, CreditCardIcon, WalletIcon, RepeatIcon } from 'lucide-react';
import { mockBills, scheduleBills } from '../mocks/mockBills';
import { mockSuppliers } from '../../suppliers/mocks/mockSuppliers';
type ScheduleType = 'due_date' | 'today' | 'next_cycle';
interface BillRowState {
  billId: string;
  paymentMethod: string;
  designatedApprover: string;
  hasError: boolean;
}
const APPROVERS = [{
  value: 'auto',
  label: 'Assign Automatically'
}, {
  value: 'Simon Verraest',
  label: 'Simon Verraest'
}, {
  value: 'John Smith',
  label: 'John Smith'
}, {
  value: 'Sarah Johnson',
  label: 'Sarah Johnson'
}];
function getSupplierPaymentMethod(supplierId: string) {
  const supplier = mockSuppliers.find(s => s.id === supplierId);
  if (!supplier) return {
    method: '',
    label: 'No payment method',
    options: []
  };
  if (supplier.payInMethod === 'card' && supplier.cardNumber) {
    const last4 = supplier.cardNumber.slice(-4);
    const brand = supplier.cardNumber.startsWith('4') ? 'Visa' : 'Mastercard';
    return {
      method: 'card',
      label: `${brand} ending in ${last4}`,
      options: [{
        value: `card_${supplierId}`,
        label: `${brand} ending in ${last4}`
      }, {
        value: 'acquiring',
        label: 'Acquiring Balance'
      }]
    };
  }
  return {
    method: 'acquiring',
    label: 'Acquiring Balance',
    options: [{
      value: 'acquiring',
      label: 'Acquiring Balance'
    }]
  };
}
function getNextCycleDate(cycleDays: number[]): {
  date: string;
  label: string;
} | null {
  if (cycleDays.length === 0) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentDay = today.getDate();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  let nextDate: Date | null = null;
  for (const day of cycleDays) {
    let candidate: Date;
    if (day === -1) {
      // Last day of current month
      const lastDayThisMonth = new Date(currentYear, currentMonth + 1, 0);
      if (lastDayThisMonth > today) {
        candidate = lastDayThisMonth;
      } else {
        // Last day of next month
        candidate = new Date(currentYear, currentMonth + 2, 0);
      }
    } else {
      // Specific day of month
      candidate = new Date(currentYear, currentMonth, day);
      if (candidate <= today) {
        // Move to next month
        candidate = new Date(currentYear, currentMonth + 1, day);
      }
    }
    if (!nextDate || candidate < nextDate) {
      nextDate = candidate;
    }
  }
  if (!nextDate) return null;
  return {
    date: nextDate.toISOString().split('T')[0],
    label: nextDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  };
}
function getPaymentDate(scheduleType: ScheduleType, dueDate: string, cycleDays?: number[]): string {
  if (scheduleType === 'today') {
    return new Date().toISOString().split('T')[0];
  }
  if (scheduleType === 'next_cycle' && cycleDays) {
    const nextCycle = getNextCycleDate(cycleDays);
    if (nextCycle) return nextCycle.date;
    // Fallback to today if no cycles configured
    return new Date().toISOString().split('T')[0];
  }
  // due_date: use the bill's due date, but if it's in the past, use today
  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (due < today) {
    return today.toISOString().split('T')[0];
  }
  return dueDate;
}
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}
export function BillApprovalWizard() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [searchParams] = useSearchParams();
  const idsParam = searchParams.get('ids');
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [scheduleType, setScheduleType] = useState<ScheduleType>('due_date');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  // Read payment cycles from localStorage
  const paymentCycles = useMemo(() => {
    const stored = localStorage.getItem('billPaymentCycles');
    return stored ? JSON.parse(stored) as number[] : [];
  }, []);
  const nextCycleInfo = useMemo(() => getNextCycleDate(paymentCycles), [paymentCycles]);
  // Get selected bills
  const selectedBills = useMemo(() => {
    if (id && id !== 'schedule') {
      return mockBills.filter(b => b.id === id);
    }
    if (idsParam) {
      const ids = idsParam.split(',');
      return mockBills.filter(b => ids.includes(b.id));
    }
    return [];
  }, [id, idsParam]);
  // Per-bill row state
  const [billRows, setBillRows] = useState<BillRowState[]>([]);
  // Initialize bill row state from supplier data
  useEffect(() => {
    if (selectedBills.length > 0 && billRows.length === 0) {
      const rows = selectedBills.map(bill => {
        const supplierPay = getSupplierPaymentMethod(bill.supplierId);
        const defaultMethod = supplierPay.options.length > 0 ? supplierPay.options[0].value : '';
        return {
          billId: bill.id,
          paymentMethod: defaultMethod,
          designatedApprover: 'auto',
          hasError: !defaultMethod
        };
      });
      setBillRows(rows);
    }
  }, [selectedBills]);
  const totalAmount = selectedBills.reduce((sum, b) => sum + b.amount, 0);
  // Validation
  const allValid = billRows.every(r => r.paymentMethod !== '');
  const invalidCount = billRows.filter(r => r.paymentMethod === '').length;
  // Redirect if no bills selected
  useEffect(() => {
    if (selectedBills.length === 0) {
      navigate('/bills');
    }
  }, [selectedBills, navigate]);
  const updateBillRow = (billId: string, updates: Partial<BillRowState>) => {
    setBillRows(prev => prev.map(r => {
      if (r.billId !== billId) return r;
      const updated = {
        ...r,
        ...updates
      };
      updated.hasError = !updated.paymentMethod;
      return updated;
    }));
  };
  const handleConfirmSchedule = () => {
    if (!allValid) {
      setShowValidationErrors(true);
      return;
    }
    const assignments: Record<string, {
      approver: string;
      paymentMethod: string;
      paymentDate: string;
    }> = {};
    billRows.forEach(row => {
      const bill = selectedBills.find(b => b.id === row.billId);
      if (bill) {
        assignments[row.billId] = {
          approver: row.designatedApprover,
          paymentMethod: row.paymentMethod,
          paymentDate: getPaymentDate(scheduleType, bill.dueDate, paymentCycles)
        };
      }
    });
    scheduleBills(billRows.map(r => r.billId), assignments);
    setStep(3);
  };
  // Compute which bills are assigned to "me" (Simon Verraest)
  const assignedToMe = billRows.filter(r => r.designatedApprover === 'Simon Verraest');
  const autoAssigned = billRows.filter(r => r.designatedApprover === 'auto');
  if (selectedBills.length === 0) return null;
  return <div className="fixed inset-0 z-50 bg-[#F8F9FA] overflow-y-auto" data-id="element-3760">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10" data-id="element-3761">
        <button onClick={() => navigate('/bills')} aria-label="Close wizard" className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors" data-id="element-3762">
          <XIcon size={20} aria-hidden="true" data-id="element-3763" />
        </button>
        {/* Progress Bar */}
        <div className="flex items-center gap-3" data-id="element-3764">
          <span className="text-xs text-gray-500 font-medium" data-id="element-3765">
            {t('pages.BillApprovalWizard.step')} {step}{' '}
            {t('pages.BillApprovalWizard.of_3')}
          </span>
          <div className="w-64 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-3766">
            <div className="h-full bg-gradient-to-r from-[#E2231A] to-[#EB4A42] transition-all duration-300 ease-in-out rounded-full" style={{
            width: step === 1 ? '33%' : step === 2 ? '66%' : '100%'
          }} data-id="element-3767" />
          </div>
        </div>
        <div className="w-10" data-id="element-3768" />
      </div>

      <div className="max-w-6xl mx-auto py-10 px-4 sm:px-6" data-id="element-3769">
        {/* ========== STEP 1: Schedule Selection ========== */}
        {step === 1 && <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 max-w-3xl mx-auto" data-id="element-3770">
            <div className="text-center mb-10" data-id="element-3771">
              <div className="w-12 h-12 bg-white rounded-full shadow-sm border border-gray-100 flex items-center justify-center mx-auto mb-6" data-id="element-3772">
                <CalendarIcon size={24} className="text-gray-900" data-id="element-3773" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2" data-id="element-3774">
                {t('pages.BillApprovalWizard.when_would_you_like_the_payments')}
              </h1>
              <p className="text-sm text-gray-500" data-id="element-3775">
                {t('pages.BillApprovalWizard.select_one_of_the_options_below')}
              </p>
            </div>

            <div className="space-y-4 mb-8" data-id="element-3776">
              <label className={`block bg-white border rounded-xl p-6 cursor-pointer transition-all ${scheduleType === 'due_date' ? 'border-[#E2231A] ring-1 ring-[#E2231A] shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3777">
                <div className="flex items-start" data-id="element-3778">
                  <div className="flex items-center h-5" data-id="element-3779">
                    <input type="radio" name="scheduleType" value="due_date" checked={scheduleType === 'due_date'} onChange={() => setScheduleType('due_date')} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-3780" />
                  </div>
                  <div className="ml-4" data-id="element-3781">
                    <span className="block text-sm font-semibold text-gray-900 mb-1" data-id="element-3782">
                      {t('pages.BillApprovalWizard.payment_scheduled_on_invoice_due_date')}
                    </span>
                    <span className="block text-xs text-gray-500 leading-relaxed" data-id="element-3783">
                      {t('pages.BillApprovalWizard.the_payments_for_the_selected_bills')}
                    </span>
                  </div>
                </div>
              </label>

              <label className={`block bg-white border rounded-xl p-6 cursor-pointer transition-all ${scheduleType === 'today' ? 'border-[#E2231A] ring-1 ring-[#E2231A] shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3784">
                <div className="flex items-start" data-id="element-3785">
                  <div className="flex items-center h-5" data-id="element-3786">
                    <input type="radio" name="scheduleType" value="today" checked={scheduleType === 'today'} onChange={() => setScheduleType('today')} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-3787" />
                  </div>
                  <div className="ml-4" data-id="element-3788">
                    <span className="block text-sm font-semibold text-gray-900 mb-1" data-id="element-3789">
                      {t('pages.BillApprovalWizard.payment_scheduled_today')}
                    </span>
                    <span className="block text-xs text-gray-500 leading-relaxed" data-id="element-3790">
                      {t('pages.BillApprovalWizard.the_payments_for_the_selected_bills_2')}
                    </span>
                  </div>
                </div>
              </label>

              {paymentCycles.length > 0 && <label className={`block bg-white border rounded-xl p-6 cursor-pointer transition-all ${scheduleType === 'next_cycle' ? 'border-[#E2231A] ring-1 ring-[#E2231A] shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3791">
                  <div className="flex items-start" data-id="element-3792">
                    <div className="flex items-center h-5" data-id="element-3793">
                      <input type="radio" name="scheduleType" value="next_cycle" checked={scheduleType === 'next_cycle'} onChange={() => setScheduleType('next_cycle')} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-3794" />
                    </div>
                    <div className="ml-4" data-id="element-3795">
                      <span className="block text-sm font-semibold text-gray-900 mb-1" data-id="element-3796">
                        {t('pages.BillApprovalWizard.payment_scheduled_on_next_cycle')}
                      </span>
                      <span className="block text-xs text-gray-500 leading-relaxed" data-id="element-3797">
                        {t('pages.BillApprovalWizard.the_payments_for_the_selected_bills_3')}
                      </span>
                      {nextCycleInfo && <div className="mt-2 p-2 bg-[#F0F2F5] rounded text-xs text-gray-600" data-id="element-3798">
                          {t('pages.BillApprovalWizard.next_cycle')}{' '}
                          {nextCycleInfo.label}
                        </div>}
                    </div>
                  </div>
                </label>}
            </div>

            <div className="text-center mb-10" data-id="element-3799">
              <p className="text-xs text-gray-500" data-id="element-3800">
                {t('pages.BillApprovalWizard.define_your_payment_cycle_to_align')}{' '}
                <button className="text-[#9F140F] font-medium hover:underline" data-id="element-3801">
                  {t('pages.BillApprovalWizard.go_to_bills_settings')}
                </button>
              </p>
            </div>

            <div className="flex justify-center gap-4" data-id="element-3802">
              <button onClick={() => navigate('/bills')} className="px-8 py-2.5 text-sm font-medium text-[#9F140F] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-3803">
                {t('pages.BillApprovalWizard.cancel')}
              </button>
              <button onClick={() => setStep(2)} className="px-8 py-2.5 text-sm font-medium text-white bg-[#E2231A] rounded-lg hover:bg-[#C41B14] transition-colors shadow-sm" data-id="element-3804">
                {t('pages.BillApprovalWizard.next')}
              </button>
            </div>
          </div>}

        {/* ========== STEP 2: Review & Confirm ========== */}
        {step === 2 && <div className="animate-in fade-in slide-in-from-right-8 duration-300" data-id="element-3805">
            <div className="text-center mb-8" data-id="element-3806">
              <div className="w-12 h-12 bg-white rounded-full shadow-sm border border-gray-100 flex items-center justify-center mx-auto mb-6" data-id="element-3807">
                <EyeIcon size={24} className="text-gray-900" data-id="element-3808" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2" data-id="element-3809">
                {t('pages.BillApprovalWizard.review_and_confirm')}
              </h1>
              <p className="text-sm text-gray-500" data-id="element-3810">
                {t('pages.BillApprovalWizard.please_review_the_details_carefully_before')}
              </p>
            </div>

            {/* Info Banner */}
            <div className="bg-[#F0F2F5] rounded-xl p-5 mb-6 flex items-start" data-id="element-3811">
              <AlertCircleIcon size={20} className="text-gray-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-3812" />

              <div data-id="element-3813">
                <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-3814">
                  {t('pages.BillApprovalWizard.how_to_approve_bill_payment_schedules')}
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed" data-id="element-3815">
                  {t('pages.BillApprovalWizard.after_submitting_the_bill_payment_schedules')}
                </p>
              </div>
            </div>

            {/* Summary Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6" data-id="element-3816">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50" data-id="element-3817">
                <h3 className="text-sm font-semibold text-gray-900" data-id="element-3818">
                  {t('pages.BillApprovalWizard.summary')}
                </h3>
              </div>
              <div className="p-6" data-id="element-3819">
                <div className="grid grid-cols-3 gap-6" data-id="element-3820">
                  <div data-id="element-3821">
                    <span className="text-xs text-gray-500 block mb-1" data-id="element-3822">
                      {t('pages.BillApprovalWizard.total_payments')}
                    </span>
                    <span className="text-lg font-semibold text-gray-900" data-id="element-3823">
                      {selectedBills.length}
                    </span>
                  </div>
                  <div data-id="element-3824">
                    <span className="text-xs text-gray-500 block mb-1" data-id="element-3825">
                      {t('pages.BillApprovalWizard.total_amount')}
                    </span>
                    <span className="text-lg font-semibold text-gray-900" data-id="element-3826">
                      {t('pages.BillApprovalWizard.usd')}{' '}
                      {totalAmount.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                    </span>
                  </div>
                  <div data-id="element-3827">
                    <span className="text-xs text-gray-500 block mb-1" data-id="element-3828">
                      {t('pages.BillApprovalWizard.payment_schedule')}
                    </span>
                    <span className="text-lg font-semibold text-gray-900" data-id="element-3829">
                      {scheduleType === 'due_date' ? 'On due date' : scheduleType === 'today' ? 'Immediately' : 'Next cycle'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payments Table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6" data-id="element-3830">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between" data-id="element-3831">
                <h3 className="text-sm font-semibold text-gray-900" data-id="element-3832">
                  {t('pages.BillApprovalWizard.payments_to_be_scheduled')}
                </h3>
                {showValidationErrors && invalidCount > 0 && <span className="text-xs text-red-500 font-medium flex items-center gap-1" data-id="element-3833">
                    <AlertTriangleIcon size={12} data-id="element-3834" />
                    {invalidCount} {t('pages.BillApprovalWizard.bill')}
                    {invalidCount !== 1 ? 's' : ''}{' '}
                    {t('pages.BillApprovalWizard.missing_payment_method')}
                  </span>}
              </div>
              <div className="overflow-x-auto" data-id="element-3835">
                <table className="w-full text-left" data-id="element-3836">
                  <thead data-id="element-3837">
                    <tr className="border-b border-gray-100" data-id="element-3838">
                      <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" data-id="element-3839">
                        {t('pages.BillApprovalWizard.bill_details')}
                      </th>
                      <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" data-id="element-3840">
                        {t('pages.BillApprovalWizard.bill_due_date')}
                      </th>
                      <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" data-id="element-3841">
                        {t('pages.BillApprovalWizard.payment_date')}
                      </th>
                      <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap text-right" data-id="element-3842">
                        {t('pages.BillApprovalWizard.amount')}
                      </th>
                      <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" style={{
                    minWidth: 200
                  }} data-id="element-3843">
                        {t('pages.BillApprovalWizard.pay_bill_with')}
                      </th>
                      <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" style={{
                    minWidth: 200
                  }} data-id="element-3844">
                        {t('pages.BillApprovalWizard.designated_approver')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-3845">
                    {selectedBills.map(bill => {
                  const rowState = billRows.find(r => r.billId === bill.id);
                  const supplierPay = getSupplierPaymentMethod(bill.supplierId);
                  const paymentDate = getPaymentDate(scheduleType, bill.dueDate);
                  const isPastDue = scheduleType === 'due_date' && new Date(bill.dueDate) < new Date();
                  const hasError = showValidationErrors && rowState?.paymentMethod === '';
                  return <tr key={bill.id} className={hasError ? 'bg-red-50/40' : ''} data-id="element-3846">
                          <td className="px-5 py-4" data-id="element-3847">
                            <div className="text-sm font-semibold text-gray-900" data-id="element-3848">
                              {bill.supplierName}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5" data-id="element-3849">
                              {bill.billNumber}
                            </div>
                          </td>
                          <td className="px-5 py-4 text-sm text-gray-900 whitespace-nowrap" data-id="element-3850">
                            {formatDate(bill.dueDate)}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap" data-id="element-3851">
                            <div className="flex items-center gap-1.5" data-id="element-3852">
                              <span className="text-sm font-medium text-gray-900" data-id="element-3853">
                                {formatDate(paymentDate)}
                              </span>
                              {isPastDue && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700" data-id="element-3854">
                                  {t('pages.BillApprovalWizard.adjusted')}
                                </span>}
                              {scheduleType === 'today' && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700" data-id="element-3855">
                                  {t('pages.BillApprovalWizard.immediate')}
                                </span>}
                            </div>
                          </td>
                          <td className="px-5 py-4 text-right whitespace-nowrap" data-id="element-3856">
                            <span className="text-sm font-semibold text-gray-900" data-id="element-3857">
                              {bill.currency}{' '}
                              {bill.amount.toLocaleString('en-US', {
                          minimumFractionDigits: 2
                        })}
                            </span>
                          </td>
                          <td className="px-5 py-4" data-id="element-3858">
                            <div className="relative" data-id="element-3859">
                              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-3860">
                                {rowState?.paymentMethod?.startsWith('card') ? <CreditCardIcon size={14} className="text-gray-400" data-id="element-3861" /> : <WalletIcon size={14} className="text-gray-400" data-id="element-3862" />}
                              </div>
                              <select value={rowState?.paymentMethod || ''} onChange={e => updateBillRow(bill.id, {
                          paymentMethod: e.target.value
                        })} className={`w-full appearance-none pl-8 pr-8 py-2 text-sm border rounded-lg focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A] bg-white ${hasError ? 'border-red-300 text-red-600' : 'border-gray-200 text-gray-900'}`} data-id="element-3863">
                                {supplierPay.options.length === 0 && <option value="" data-id="element-3864">
                                    {t('pages.BillApprovalWizard.no_payment_method_configured')}
                                  </option>}
                                {supplierPay.options.map(opt => <option key={opt.value} value={opt.value} data-id="element-3865">
                                    {opt.label}
                                  </option>)}
                              </select>
                              <ChevronDownIcon size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-3866" />
                            </div>
                            {hasError && <p className="text-[10px] text-red-500 mt-1" data-id="element-3867">
                                {t('pages.BillApprovalWizard.payment_method_required')}
                              </p>}
                          </td>
                          <td className="px-5 py-4" data-id="element-3868">
                            <div className="relative" data-id="element-3869">
                              <select value={rowState?.designatedApprover || 'auto'} onChange={e => updateBillRow(bill.id, {
                          designatedApprover: e.target.value
                        })} className="w-full appearance-none px-3 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A] bg-white" data-id="element-3870">
                                {APPROVERS.map(a => <option key={a.value} value={a.value} data-id="element-3871">
                                    {a.label}
                                  </option>)}
                              </select>
                              <ChevronDownIcon size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-3872" />
                            </div>
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100" data-id="element-3873">
                <p className="text-xs text-gray-500 mb-1" data-id="element-3874">
                  {t('pages.BillApprovalWizard.after_confirming_the_payment_method_and')}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3875">
                  {t('pages.BillApprovalWizard.your_selected_payment_schedule_is')}{' '}
                  <span className="font-semibold text-gray-700" data-id="element-3876">
                    {scheduleType === 'due_date' ? 'On the billing due date' : scheduleType === 'today' ? 'Immediately (today)' : 'On the next cycle'}
                  </span>
                </p>
              </div>
            </div>

            {/* Validation error */}
            {showValidationErrors && !allValid && <div className="text-center mb-6" data-id="element-3877">
                <p className="text-xs text-red-500 flex items-center justify-center gap-1.5" data-id="element-3878">
                  <AlertTriangleIcon size={14} data-id="element-3879" />
                  {t('pages.BillApprovalWizard.unable_to_schedule_payments_please_ensure')}
                </p>
              </div>}

            <div className="flex justify-center gap-4" data-id="element-3880">
              <button onClick={() => setStep(1)} className="px-8 py-2.5 text-sm font-medium text-[#9F140F] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-3881">
                {t('pages.BillApprovalWizard.back')}
              </button>
              <button onClick={handleConfirmSchedule} className={`px-8 py-2.5 text-sm font-medium rounded-lg transition-colors shadow-sm ${allValid ? 'bg-[#E2231A] hover:bg-[#C41B14] text-white' : 'bg-[#E2231A]/80 text-white hover:bg-[#C41B14]'}`} data-id="element-3882">
                {t('pages.BillApprovalWizard.confirm_and_schedule')}
              </button>
            </div>
          </div>}

        {/* ========== STEP 3: Success ========== */}
        {step === 3 && <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 max-w-2xl mx-auto text-center" data-id="element-3883">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-3884">
              <CheckCircleIcon size={32} className="text-green-600" data-id="element-3885" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-3" data-id="element-3886">
              {t('pages.BillApprovalWizard.payments_scheduled_successfully')}
            </h1>
            <p className="text-sm text-gray-500 mb-10 max-w-md mx-auto" data-id="element-3887">
              {selectedBills.length} {t('pages.BillApprovalWizard.bill')}
              {selectedBills.length !== 1 ? 's have' : ' has'}{' '}
              {t('pages.BillApprovalWizard.been_submitted_for_approval_approvers_will')}
            </p>

            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10" data-id="element-3888">
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-left" data-id="element-3889">
                <span className="text-xs text-gray-500 block mb-1" data-id="element-3890">
                  {t('pages.BillApprovalWizard.total_scheduled')}
                </span>
                <span className="text-lg font-bold text-gray-900" data-id="element-3891">
                  {t('pages.BillApprovalWizard.usd')}{' '}
                  {totalAmount.toLocaleString('en-US', {
                minimumFractionDigits: 2
              })}
                </span>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-left" data-id="element-3892">
                <span className="text-xs text-gray-500 block mb-1" data-id="element-3893">
                  {t('pages.BillApprovalWizard.assigned_to_you')}
                </span>
                <span className="text-lg font-bold text-gray-900" data-id="element-3894">
                  {assignedToMe.length} {t('pages.BillApprovalWizard.bill')}
                  {assignedToMe.length !== 1 ? 's' : ''}
                </span>
                {assignedToMe.length > 0 && <p className="text-[10px] text-[#9F140F] mt-1 font-medium" data-id="element-3895">
                    {t('pages.BillApprovalWizard.view_under_assigned_to_me_tab')}
                  </p>}
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-left" data-id="element-3896">
                <span className="text-xs text-gray-500 block mb-1" data-id="element-3897">
                  {t('pages.BillApprovalWizard.autoassigned')}
                </span>
                <span className="text-lg font-bold text-gray-900" data-id="element-3898">
                  {autoAssigned.length} {t('pages.BillApprovalWizard.bill')}
                  {autoAssigned.length !== 1 ? 's' : ''}
                </span>
                <p className="text-[10px] text-gray-400 mt-1" data-id="element-3899">
                  {t('pages.BillApprovalWizard.based_on_threshold_rules')}
                </p>
              </div>
            </div>

            {/* Scheduled bills detail */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-10 text-left" data-id="element-3900">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50" data-id="element-3901">
                <h3 className="text-sm font-semibold text-gray-900" data-id="element-3902">
                  {t('pages.BillApprovalWizard.scheduled_payments')}
                </h3>
              </div>
              <div className="divide-y divide-gray-100" data-id="element-3903">
                {selectedBills.map(bill => {
              const rowState = billRows.find(r => r.billId === bill.id);
              const paymentDate = getPaymentDate(scheduleType, bill.dueDate);
              const approverLabel = rowState?.designatedApprover === 'auto' ? 'Auto-assigned' : rowState?.designatedApprover || 'Auto-assigned';
              return <div key={bill.id} className="px-6 py-4 flex items-center justify-between" data-id="element-3904">
                      <div className="flex items-center gap-4 min-w-0" data-id="element-3905">
                        <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-3906">
                          <CheckCircleIcon size={16} className="text-green-500" data-id="element-3907" />
                        </div>
                        <div className="min-w-0" data-id="element-3908">
                          <div className="text-sm font-medium text-gray-900 truncate" data-id="element-3909">
                            {bill.supplierName}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-3910">
                            {bill.billNumber}{' '}
                            {t('pages.BillApprovalWizard.payment_on')}{' '}
                            {formatDate(paymentDate)}{' '}
                            {t('pages.BillApprovalWizard.approver')}{' '}
                            {approverLabel}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-gray-900 flex-shrink-0 ml-4" data-id="element-3911">
                        {bill.currency}{' '}
                        {bill.amount.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                      </div>
                    </div>;
            })}
              </div>
            </div>

            <div className="flex justify-center gap-4" data-id="element-3912">
              <button onClick={() => navigate('/bills')} className="px-8 py-2.5 text-sm font-medium text-white bg-[#E2231A] rounded-lg hover:bg-[#C41B14] transition-colors shadow-sm" data-id="element-3913">
                {t('pages.BillApprovalWizard.back_to_bills')}
              </button>
            </div>
          </div>}
      </div>
    </div>;
}